import { SpecifierName, Sports } from '@gin-betting/constants';
import { App, View } from '@gin-betting/core';
import { without } from 'lodash';

import { LIVE_SPORTEVENT_STATUSES } from 'app-constants';
import isMetaTypeEqualScoreType from 'utils/isMetaTypeEqualScoreType';
import type { GetMetaPart, ScoreConfig, SetMatchPart } from './types';

const emptyMeta: View.Meta = { name: '', value: '', id: '' };

const getMetaPartIceHockeyFootball: GetMetaPart = (meta, sportId) => {
    const config = CONFIG_BY_SPORTS[sportId];

    const period = createPeriod(meta);

    if (period && config?.timeSort?.[period.name]) return period;

    return emptyMeta;
};

function makeCreateMetaByType([type, typeNumber]: [
    App.MetaTypes,
    App.MetaTypes
]) {
    return (meta: View.Meta[]): View.Meta | undefined => {
        const metaType = meta.find(({ name }) => name === type)?.value;
        const metaTypeValue = meta.find(({ name }) => name === typeNumber)
            ?.value;

        if (!metaType || !metaTypeValue) return;

        return {
            name: metaType,
            value: metaTypeValue,
        };
    };
}

const createPeriod = makeCreateMetaByType([
    App.MetaTypes.TYPE_PERIOD,
    App.MetaTypes.TYPE_PERIOD_NUMBER,
]);

const createGameState = makeCreateMetaByType([
    App.MetaTypes.TYPE_GAME_STATE,
    App.MetaTypes.TYPE_STATE_NUMBER,
]);

const getMetaPartEsportsFifa: GetMetaPart = (meta, sportId) => {
    const config = CONFIG_BY_SPORTS[sportId];

    const period = createPeriod(meta);

    if (period && config?.timeSort?.[period.name]) return period;

    if (period && config?.insteadMeta?.includes(period.name as App.MetaTypes)) {
        return {
            name: App.MetaTypes.TYPE_PERIOD_2ND_HALF,
            value: '',
        };
    }

    return emptyMeta;
};

const getMetaPartEsportsIceHockey: GetMetaPart = (meta, sportId) => {
    const config = CONFIG_BY_SPORTS[sportId];

    const period = createPeriod(meta);

    if (period && config?.timeSort?.[period.name]) return period;

    if (period && config?.insteadMeta?.includes(period.name as App.MetaTypes)) {
        return { name: App.MetaTypes.TYPE_REGULAR, value: '3' };
    }

    return emptyMeta;
};

const getMetaPartEsportsRocketLeagueCounterStrikeRainbowSix: GetMetaPart = (
    meta,
    sportId
) => {
    const config = CONFIG_BY_SPORTS[sportId];

    const gameState = createGameState(meta);

    if (gameState && config?.timeSort?.[gameState.name]) {
        return gameState;
    }

    return emptyMeta;
};

const tennisSetMatchPartTypes: SetMatchPart = ({
    score,
    currentMatchPart,
    isOverview,
}) => {
    const isNumerable = App.ScoreTypesNumerable.includes(score.type);

    const result = {
        scoreTypesTitleMapping: SCORE_TYPES_TITLE_MAPPING,
        isNumerable,
    };

    if (isOverview)
        return {
            scoreTypesTitleMapping: {
                ...SCORE_TYPES_TITLE_MAPPING,
                [App.ScoreTypes.TYPE_SET]: '',
                [App.ScoreTypes.TYPE_GAME_POINTS]: 'P',
            },
            isNumerable,
        };

    if (!currentMatchPart) return result;

    const { type, indexNumber, status } = currentMatchPart;

    const isCurrentPart =
        LIVE_SPORTEVENT_STATUSES.includes(status) &&
        isMetaTypeEqualScoreType(type, score.type) &&
        score.indexNumber === indexNumber;

    if (!isCurrentPart) return result;

    return {
        scoreTypesTitleMapping: {
            ...SCORE_TYPES_TITLE_MAPPING,
            [App.ScoreTypes.TYPE_SET]: 'GS',
        },
        isNumerable: false,
    };
};

const dota2MatchPartTypes: SetMatchPart = ({ score, isOverview }) => ({
    scoreTypesTitleMapping: {
        ...SCORE_TYPES_TITLE_MAPPING,
        ...(isOverview && {
            [App.ScoreTypes.TYPE_MAP]: '',
        }),
    },
    isNumerable: App.ScoreTypesNumerable.includes(score.type),
});
const basketballMatchPartTypes: SetMatchPart = ({ score, isOverview }) => ({
    scoreTypesTitleMapping: {
        ...SCORE_TYPES_TITLE_MAPPING,
        ...(isOverview && {
            [App.ScoreTypes.TYPE_QUARTER]: '',
        }),
    },
    isNumerable: App.ScoreTypesNumerable.includes(score.type),
});

export const SCORE_TYPES_TITLE_MAPPING: Record<App.ScoreTypes, string> = {
    [App.ScoreTypes.TYPE_TOTAL]: 'Score',
    [App.ScoreTypes.TYPE_GAME_POINTS]: 'GP',
    [App.ScoreTypes.TYPE_SET]: 'S',
    [App.ScoreTypes.TYPE_TIE_BREAK]: 'TBP',
    [App.ScoreTypes.TYPE_GOLDEN_SET]: 'GS',
    [App.ScoreTypes.TYPE_YELLOW_CARD]: 'YC',
    [App.ScoreTypes.TYPE_RED_CARD]: 'RC',
    [App.ScoreTypes.TYPE_YELLOW_RED_CARD]: 'YRC',
    [App.ScoreTypes.TYPE_PERIOD_1ST_HALF]: '1H',
    [App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA]: '1E',
    [App.ScoreTypes.TYPE_PERIOD_2ND_HALF]: '2H',
    [App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA]: '2E',
    [App.ScoreTypes.TYPE_PERIOD_PENALTIES]: 'P',
    [App.ScoreTypes.TYPE_QUARTER]: 'Q',
    [App.ScoreTypes.TYPE_QUARTER_OVERTIME]: 'OT',
    [App.ScoreTypes.TYPE_PERIOD_REGULAR]: 'P',
    [App.ScoreTypes.TYPE_PERIOD_OVERTIME]: 'OT',
    [App.ScoreTypes.TYPE_PERIOD_BULLETS]: 'B',
    [App.ScoreTypes.TYPE_GAME_KILLS]: 'K',
    [App.ScoreTypes.TYPE_GAME_PLAYERS]: 'Players',
    [App.ScoreTypes.TYPE_MAP]: 'Map',
    [App.ScoreTypes.TYPE_ROUND_OVERTIME]: 'OT',
    [App.ScoreTypes.TYPE_INNING_POINTS]: 'IN',
    [App.ScoreTypes.TYPE_INNING_OUTS]: 'INO',
};

export const SPECIFIER_SCORE_TYPES_MAPPING = {
    [SpecifierName.inningnr]: App.ScoreTypes.TYPE_INNING_POINTS,
    [SpecifierName.quarternr]: App.ScoreTypes.TYPE_QUARTER,
    [SpecifierName.setnr]: App.ScoreTypes.TYPE_SET,
    [SpecifierName.gamenr]: App.ScoreTypes.TYPE_GAME_POINTS,
    [SpecifierName.mapnr]: App.ScoreTypes.TYPE_MAP,
    [SpecifierName.periodnr]: App.ScoreTypes.TYPE_PERIOD_REGULAR,
};

export const metaTypesNumerable = without(
    App.MetaTypesNumerable,
    App.MetaTypes.TYPE_OVERTIME,
    App.MetaTypes.TYPE_BULLETS
);

const handballScoreTitleMapping = {
    ...SCORE_TYPES_TITLE_MAPPING,
    [App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA]: '1ET',
    [App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA]: '2ET',
};

const footballOverviewTitleMapping = {
    ...SCORE_TYPES_TITLE_MAPPING,
    [App.ScoreTypes.TYPE_PERIOD_1ST_HALF]: '1',
    [App.ScoreTypes.TYPE_PERIOD_2ND_HALF]: '2',
};

const handballMatchPart: SetMatchPart = ({ score }) => {
    const isNumerable = App.ScoreTypesNumerable.includes(score.type);

    return {
        scoreTypesTitleMapping: handballScoreTitleMapping,
        isNumerable,
    };
};

const footballMatchPart: SetMatchPart = ({ score, isOverview }) => {
    const isNumerable = App.ScoreTypesNumerable.includes(score.type);

    return {
        scoreTypesTitleMapping: isOverview
            ? footballOverviewTitleMapping
            : SCORE_TYPES_TITLE_MAPPING,
        isNumerable,
    };
};

const defaultTitleParser: NonNullable<ScoreConfig['titleParser']> = ({
    index,
    text,
}) => `${text} ${index}`;

const iceHockeyTennisTitleParser: NonNullable<ScoreConfig['titleParser']> = ({
    index,
    text,
    isOverview,
}) => (isOverview ? `${index}` : `${index} ${text}`);

const defaultBasketballTitleParser: NonNullable<ScoreConfig['titleParser']> = ({
    index,
    text,
}) => `${index} ${text}`;

const counterStrikeTitleParser: NonNullable<ScoreConfig['titleParser']> = ({
    index,
    meta,
    text,
    type,
}) => {
    const defaultResult = defaultTitleParser({
        index,
        meta,
        text,
        type,
    });

    if (![App.MetaTypes.TYPE_MAP, App.ScoreTypes.TYPE_MAP].includes(type)) {
        return defaultResult;
    }

    const map = meta.find(({ name }) => name === App.MetaTypes.TYPE_MAP);

    if (!map) {
        return defaultResult;
    }

    let mapsMapping: { [key: number]: string } = {};

    try {
        mapsMapping = JSON.parse(map.value);
    } catch (e) {}

    const mapId = mapsMapping[index];

    if (!mapId || mapId.toLocaleLowerCase() === 'unknown') return defaultResult;

    return mapId;
};

const basketballTitleParser: NonNullable<ScoreConfig['titleParser']> = ({
    index,
    meta,
    text,
    type,
}) => {
    const defaultResult = defaultTitleParser({
        index,
        meta,
        text,
        type,
    });

    const defaultBasketball = defaultBasketballTitleParser({
        index,
        meta,
        text,
        type,
    });

    if (
        ![
            App.MetaTypes.TYPE_OVERTIME,
            App.ScoreTypes.TYPE_QUARTER_OVERTIME,
        ].includes(type)
    ) {
        return defaultBasketball;
    }

    return defaultResult;
};

export const CONFIG_BY_SPORTS: {
    [sportId: string]: ScoreConfig;
} = {
    [Sports.ICE_HOCKEY]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_PERIOD_REGULAR]: 2,
            [App.ScoreTypes.TYPE_PERIOD_BULLETS]: 3,
            [App.ScoreTypes.TYPE_PERIOD_OVERTIME]: 4,
        },
        timeSort: {
            [App.MetaTypes.TYPE_REGULAR]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
            [App.MetaTypes.TYPE_BULLETS]: 3,
        },
        customMetaTypesNaming: {
            [App.MetaTypes.TYPE_REGULAR]: 'Period',
            [App.MetaTypes.TYPE_BULLETS]: 'Shootout',
            [App.MetaTypes.TYPE_OVERTIME]: 'Overtime',
        },
        partStartsFromZero: [App.MetaTypes.TYPE_REGULAR],
        customMetaTypesNumerable: without(
            App.MetaTypesNumerable,
            App.MetaTypes.TYPE_BULLETS,
            App.MetaTypes.TYPE_OVERTIME
        ),
        isTimeInSeconds: true,
        isTimeCountdown: true,
        getMetaPart: getMetaPartIceHockeyFootball,
        titleParser: iceHockeyTennisTitleParser,
    },
    [Sports.VOLLEYBALL]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GOLDEN_SET]: 3,
        },
        partStartsFromZero: [App.MetaTypes.TYPE_SET],
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
            [App.MetaTypes.TYPE_GOLDEN_SET]: 2,
        },
    },
    [Sports.BASKETBALL]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_QUARTER]: 2,
            [App.ScoreTypes.TYPE_GAME_POINTS]: 3,
        },
        partStartsFromZero: [App.MetaTypes.TYPE_QUARTER],
        timeSort: {
            [App.MetaTypes.TYPE_QUARTER]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
        },
        isTimeInSeconds: true,
        isTimeCountdown: true,
        customMetaTypesNumerable: App.MetaTypesNumerable,
        titleParser: basketballTitleParser,
        setMatchPartTypes: basketballMatchPartTypes,
    },
    [Sports.FOOTBALL]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_RED_CARD]: 2,
            [App.ScoreTypes.TYPE_PERIOD_1ST_HALF]: 3,
            [App.ScoreTypes.TYPE_PERIOD_2ND_HALF]: 4,
            [App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA]: 5,
            [App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA]: 6,
            [App.ScoreTypes.TYPE_PERIOD_PENALTIES]: 7,
        },
        timeSort: {
            [App.MetaTypes.TYPE_PERIOD_1ST_HALF]: 1,
            [App.MetaTypes.TYPE_PERIOD_2ND_HALF]: 2,
            [App.MetaTypes.TYPE_PERIOD_1ST_EXTRA]: 3,
            [App.MetaTypes.TYPE_PERIOD_2ND_EXTRA]: 4,
            [App.MetaTypes.TYPE_PERIOD_PENALTIES]: 5,
        },
        customMetaTypesNaming: {
            [App.MetaTypes.TYPE_PERIOD_1ST_HALF]: '1 Time',
            [App.MetaTypes.TYPE_PERIOD_2ND_HALF]: '2 Time',
            [App.MetaTypes.TYPE_PERIOD_1ST_EXTRA]: '1 Overtime',
            [App.MetaTypes.TYPE_PERIOD_2ND_EXTRA]: '2 Overtime',
            [App.MetaTypes.TYPE_PERIOD_PENALTIES]: 'Penalty',
        },
        isTimeInSeconds: true,
        getMetaPart: getMetaPartIceHockeyFootball,
        setMatchPartTypes: footballMatchPart,
    },
    [Sports.TENNIS]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GAME_POINTS]: 3,
            [App.ScoreTypes.TYPE_TIE_BREAK]: 4,
        },
        partStartsFromZero: [App.MetaTypes.TYPE_SET],
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
        },
        customMetaTypesNaming: {
            [App.MetaTypes.TYPE_SET]: 'Set',
        },
        setMatchPartTypes: tennisSetMatchPartTypes,
        titleParser: iceHockeyTennisTitleParser,
    },
    [Sports.HANDBALL]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_PERIOD_1ST_HALF]: 2,
            [App.ScoreTypes.TYPE_PERIOD_2ND_HALF]: 3,
            [App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA]: 4,
            [App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA]: 5,
        },
        timeSort: {
            [App.MetaTypes.TYPE_PERIOD_1ST_HALF]: 1,
            [App.MetaTypes.TYPE_PERIOD_2ND_HALF]: 2,
            [App.MetaTypes.TYPE_PERIOD_1ST_EXTRA]: 3,
            [App.MetaTypes.TYPE_PERIOD_2ND_EXTRA]: 4,
            [App.MetaTypes.TYPE_PERIOD_PENALTIES]: 5,
        },
        customMetaTypesNaming: {
            [App.MetaTypes.TYPE_PERIOD_1ST_EXTRA]: '1ET',
            [App.MetaTypes.TYPE_PERIOD_2ND_EXTRA]: '2ET',
        },
        isTimeInSeconds: true,
        customMetaTypesTimeDisabled: [App.MetaTypes.TYPE_PERIOD_PENALTIES],
        getMetaPart: getMetaPartIceHockeyFootball,
        setMatchPartTypes: handballMatchPart,
    },
    [Sports.TABLE_TENNIS]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GAME_POINTS]: 3,
            [App.ScoreTypes.TYPE_TIE_BREAK]: 4,
        },
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
        },
        isTimeDisabled: true,
    },
    [Sports.BEACH_VOLLEYBALL]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GOLDEN_SET]: 3,
        },
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
            [App.MetaTypes.TYPE_GOLDEN_SET]: 2,
        },
        isTimeDisabled: true,
    },
    [Sports.BADMINTON]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GOLDEN_SET]: 3,
        },
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
            [App.MetaTypes.TYPE_GOLDEN_SET]: 2,
        },
        isTimeDisabled: true,
    },
    [Sports.BASEBALL]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_INNING_POINTS]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_INNING]: 1,
        },
    },

    [Sports.ESPORTS_DOTA_2]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
        },
        titleParser: defaultTitleParser,
        isTimeInSeconds: true,
        setMatchPartTypes: dota2MatchPartTypes,
    },
    [Sports.ESPORTS_FIFA]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_PERIOD_1ST_HALF]: 2,
            [App.ScoreTypes.TYPE_PERIOD_2ND_HALF]: 3,
        },
        timeSort: {
            [App.MetaTypes.TYPE_PERIOD_1ST_HALF]: 1,
            [App.MetaTypes.TYPE_PERIOD_2ND_HALF]: 2,
            [App.MetaTypes.TYPE_PERIOD]: 5,
        },
        isTimeInSeconds: true,
        insteadMeta: [
            App.MetaTypes.TYPE_PERIOD_1ST_EXTRA,
            App.MetaTypes.TYPE_PERIOD_2ND_EXTRA,
            App.MetaTypes.TYPE_PERIOD_PENALTIES,
        ],
        getMetaPart: getMetaPartEsportsFifa,
    },
    [Sports.ESPORTS_BASKETBALL]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_QUARTER]: 2,
            [App.ScoreTypes.TYPE_QUARTER_OVERTIME]: 3,
        },
        timeSort: {
            [App.MetaTypes.TYPE_QUARTER]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
        },
        isTimeInSeconds: true,
        isTimeCountdown: true,
        customMetaTypesNumerable: without(
            App.MetaTypesNumerable,
            App.MetaTypes.TYPE_BULLETS
        ),
    },
    [Sports.ESPORTS_ICE_HOCKEY]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_PERIOD_REGULAR]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_REGULAR]: 1,
        },
        isTimeInSeconds: true,
        isTimeCountdown: true,
        insteadMeta: [App.MetaTypes.TYPE_BULLETS, App.MetaTypes.TYPE_OVERTIME],
        getMetaPart: getMetaPartEsportsIceHockey,
    },
    [Sports.ESPORTS_COUNTER_STRIKE]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
        },
        titleParser: counterStrikeTitleParser,
        getMetaPart: getMetaPartEsportsRocketLeagueCounterStrikeRainbowSix,
    },
    [Sports.ESPORTS_LEAGUE_OF_LEGENDS]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
        },
        titleParser: defaultTitleParser,
        isTimeInSeconds: true,
    },
    [Sports.ESPORTS_VALORANT]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
        },
        titleParser: defaultTitleParser,
        isTimeDisabled: true,
    },
    [Sports.ESPORTS_RAINBOW_SIX]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
        },
        titleParser: defaultTitleParser,
        getMetaPart: getMetaPartEsportsRocketLeagueCounterStrikeRainbowSix,
        isTimeDisabled: true,
    },
    [Sports.ESPORTS_ROCKET_LEAGUE]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
        },
        titleParser: defaultTitleParser,
        getMetaPart: getMetaPartEsportsRocketLeagueCounterStrikeRainbowSix,
        isTimeInSeconds: true,
        isTimeCountdown: true,
        isTimeAbsolute: true,
    },
    [Sports.ESPORTS_TENNIS]: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GAME_POINTS]: 3,
            [App.ScoreTypes.TYPE_TIE_BREAK]: 4,
        },
        partStartsFromZero: [App.MetaTypes.TYPE_SET],
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
        },
        customMetaTypesNaming: {
            [App.MetaTypes.TYPE_SET]: 'Set',
        },
        setMatchPartTypes: tennisSetMatchPartTypes,
        titleParser: iceHockeyTennisTitleParser,
    },
};
